import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button, Modal, Row, Col, message, Tooltip } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { TasksInAssetListType, TasksOrderBy } from '@redux/types/tasks';
import { AssetItemDto, AssetVersionItemDto, TaskItemDto } from '@api/Api';
import { useTypedSelector } from '@hooks';
import PanelSection from '@pages/MediaViewer/components/PanelSection';
import { FilterSortPanel } from '@pages/MediaViewer/components/FilterSortPanel';
import NoResults from '@pages/MediaViewer/components/NoResults';
import TaskItem from '@pages/MediaViewer/Tasks/components/TaskItem';
import { exportAsCSV, exportAsXML } from '@helpers/exportTasks';
import {
  loadMoreAssetVersionTasks,
  openNewTaskModal,
  setAssetVersionListNeedRefresh,
  setAssetVersionListParams
} from '@redux/reducers/tasks';
import { nonNull } from '@helpers/non-null';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { useFetch } from '@hooks/useFetch';
import { assetVersionStateSelector } from '@redux/selectors/tasks';
import {
  fetchTaskListInAssetVerion,
  fetchTasksCountInAssetVerion
} from '@redux/actions/tasks';
import { useCurrentWorkspace } from '@hooks/workspace';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import TaskListSkeleton from '@components/Tasks/TaskListSkeleton';
import SkeletonPresence from '@components/SkeletonPresence';
import { useCarriedEmptyState } from '@pages/MediaViewer/hooks/useCarriedEmptyState';
import { useDeleteTask } from '@hooks/tasks';
import store from '@redux/store';
import { MediaViewerOutletContextState } from '@layouts/types';
import {
  CheckCircleFillIcon,
  CrossIcon,
  DownloadInvoiceIcon,
  PlusIcon
} from '@icons';

import './Tasks.less';

type ITasksProps = {
  onToggleOpenSelectedCollapse(value: string | null): void;
  canCreateTask: boolean;
  selectedVersion: AssetVersionItemDto;
  asset: AssetItemDto;
  isPanelPinned?: boolean;
  onTogglePanelPin?: () => void;
};

const sortMenu: ItemType[] = [
  { label: 'Date created', key: 'createdAt:DESC' },
  {
    label: 'Date modified',
    key: 'lastEventTime:DESC'
  }
];

const tasksMenu = [
  { label: 'All tasks', key: 'all' },
  { label: 'Assigned to me', key: 'assigned' },
  { label: 'Completed tasks', key: 'completed' },
  { label: 'In Progress', key: 'in-progress' }
];

function Tasks({
  onToggleOpenSelectedCollapse,
  canCreateTask,
  selectedVersion,
  asset,
  isPanelPinned,
  onTogglePanelPin
}: ITasksProps) {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const { removeComment, commentsList } =
    useOutletContext<MediaViewerOutletContextState>();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const { workspaceId } = asset;
  const assetVersionId = selectedVersion.id;

  const orderBy = useTypedSelector(
    (state) => assetVersionStateSelector(state, { assetVersionId }).orderBy
  );
  const searchQuery = useTypedSelector(
    (state) => assetVersionStateSelector(state, { assetVersionId }).searchQuery
  );
  const listType = useTypedSelector(
    (state) => assetVersionStateSelector(state, { assetVersionId }).listType
  );
  const hideResolvedTasks = useTypedSelector(
    (state) =>
      assetVersionStateSelector(state, { assetVersionId }).hideResolvedTasks
  );
  const count = useTypedSelector(
    (state) => assetVersionStateSelector(state, { assetVersionId }).count
  );
  const taskIds = useTypedSelector(
    (state) => assetVersionStateSelector(state, { assetVersionId }).taskIds
  );
  const initiallyLoaded = useTypedSelector(
    (state) =>
      assetVersionStateSelector(state, { assetVersionId }).fetch.initiallyLoaded
  );
  const hasMore = useTypedSelector(
    (state) => assetVersionStateSelector(state, { assetVersionId }).hasMore
  );
  // Previous asset had no tasks: keep its empty state while loading
  const isEmptyShownWhileLoading = useCarriedEmptyState(
    'tasks',
    initiallyLoaded ? !taskIds.length : null
  );
  const showEmpty =
    (initiallyLoaded && !taskIds.length) || isEmptyShownWhileLoading;

  useFetch({
    key: `tasks-asset-version-count-${assetVersionId}`,
    selector: (state) =>
      assetVersionStateSelector(state, { assetVersionId }).countFetch,
    fetch: (fetchType) =>
      dispatch(
        fetchTasksCountInAssetVerion({ workspaceId, assetVersionId, fetchType })
      )
  });

  const loading = useFetch({
    key: `tasks-asset-version-list-${assetVersionId}`,
    selector: (state) =>
      assetVersionStateSelector(state, { assetVersionId }).fetch,
    fetch: (fetchType) =>
      dispatch(
        fetchTaskListInAssetVerion({ workspaceId, assetVersionId, fetchType })
      )
  });

  useEffect(() => {
    dispatch(setAssetVersionListNeedRefresh({ assetVersionId }));
  }, [assetVersionId, dispatch]);

  const onLoadMore = useCallback(() => {
    dispatch(loadMoreAssetVersionTasks({ assetVersionId }));
  }, [assetVersionId, dispatch]);

  const [isLoadingDeleteComment, setIsLoadingDeleteComment] =
    useState<boolean>(false);
  const [selectedDeleteCommentId, setSelectedDeleteCommentId] = useState<
    string | null
  >();

  const deleteTask = useDeleteTask();
  const onRemoveTask = useCallback(
    async (task: TaskItemDto) => {
      try {
        await deleteTask({ id: task.id });
        const commentId = task?.assetVersionComment?.id;
        if (commentId) {
          const comment = commentsList.find((item) => item?.id === commentId);
          if (comment && comment?.user?.me)
            setSelectedDeleteCommentId(commentId);
        }
      } catch (error: any) {
        message.error({
          content: error?.response?.data?.message || 'Something went wrong',
          className: 'message-dark-modal'
        });
      }
    },
    [commentsList, deleteTask]
  );

  const onRemoveCommentById = async () => {
    if (selectedDeleteCommentId) {
      try {
        setIsLoadingDeleteComment(true);
        await removeComment(selectedDeleteCommentId);
        setSelectedDeleteCommentId(null);
      } catch (error: any) {
        message.error({
          content: error?.response?.data?.message || 'Something went wrong',
          className: 'message-dark-modal'
        });
      } finally {
        setIsLoadingDeleteComment(false);
      }
    }
  };

  const createTask = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    if (!asset || !selectedVersion) return;
    dispatch(
      openNewTaskModal({
        organizationId: asset.organizationId,
        workspaceId: asset.workspaceId,
        campaign: {
          id: asset.campaignId,
          name: asset.campaignName
        },
        asset: {
          id: asset.id,
          isDeleted: !!asset.deletedAt,
          path: asset.path,
          lastVersion: {
            ...nonNull(asset.asset).versions[0],
            type: nonNull(asset.asset).versions[0].metadata.type
          },
          selectedVersion: {
            ...selectedVersion,
            type: selectedVersion.metadata.type
          }
        },
        descriptionPlaceholder:
          'Describe your task so your teammates know what to do.'
      })
    );
  };

  const assetVersionIdRef = useRef(assetVersionId);
  assetVersionIdRef.current = assetVersionId;

  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;

  const taskIdsRef = useRef(taskIds);
  taskIdsRef.current = taskIds;

  const onExport = async (value: string) => {
    while (hasMoreRef.current) {
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      if (assetVersionIdRef.current !== assetVersionId) return;
      if (!loadingRef.current) {
        dispatch(loadMoreAssetVersionTasks({ assetVersionId }));
      }
    }
    const taskIds = taskIdsRef.current;
    if (!taskIds.length) return;
    const state = store.getState().tasks;
    const tasks = taskIds.map((id) => nonNull(state.items[id]?.entity));
    if (value === 'XML') {
      exportAsXML({
        workspaceName: currentWorkspace?.name ?? workspaceId,
        tasksList: tasks
      });
    }
    if (value === 'CSV') {
      exportAsCSV({
        workspaceName: currentWorkspace?.name ?? workspaceId,
        tasksList: tasks
      });
    }
  };

  const onToggleOpenSelectedCollapseRef = useRef(onToggleOpenSelectedCollapse);
  onToggleOpenSelectedCollapseRef.current = onToggleOpenSelectedCollapse;
  const onOpenComment = useCallback(
    (commentId: string) => {
      navigate({
        search: `commentId=${commentId}`
      });
      onToggleOpenSelectedCollapseRef.current('comments');
    },
    [navigate]
  );

  const dropdownMenuItems: ItemType[] = [];

  if (count > 0) {
    dropdownMenuItems.push({
      label: 'Export tasks as',
      icon: <DownloadInvoiceIcon />,
      key: 'submenu',
      popupClassName: 'media_viewer_submenu media_viewer_mobile-overlay',
      popupOffset: [12, 0],
      onTitleClick: ({ domEvent }) => domEvent.stopPropagation(),
      children: [
        { label: 'CSV', key: 'CSV' },
        { label: 'XML', key: 'XML' }
      ]
    });
  }
  if (listType !== 'completed' && listType !== 'in-progress') {
    dropdownMenuItems.push({
      key: 'resolved-tasks',
      icon: <CheckCircleFillIcon />,
      label: hideResolvedTasks ? 'Show resolved tasks' : 'Hide resolved tasks',
      onClick: () =>
        dispatch(
          setAssetVersionListParams({
            assetVersionId,
            hideResolvedTasks: !hideResolvedTasks
          })
        )
    });
  }

  return (
    <PanelSection
      name="Tasks"
      dropdownMenu={dropdownMenuItems.length ? dropdownMenuItems : undefined}
      onDropdownHandler={onExport}
      onClose={() => onToggleOpenSelectedCollapseRef.current(null)}
      isPinned={isPanelPinned}
      onTogglePin={onTogglePanelPin}
      count={count || 0}
      extraction={
        canCreateTask ? (
          <Tooltip
            title="Add new task"
            overlayClassName="media_viewer_tooltip"
            placement="leftBottom"
          >
            <Button
              className="media_viewer_btn b-btn-plus b-only-icon"
              onClick={createTask}
            >
              <PlusIcon />
            </Button>
          </Tooltip>
        ) : null
      }
    >
      <>
        <FilterSortPanel
          orderBy={orderBy}
          sortMenu={sortMenu}
          onDropdownHandler={(listType: TasksInAssetListType) => {
            dispatch(
              setAssetVersionListParams({
                assetVersionId,
                listType
              })
            );
          }}
          onSortHandler={(orderBy: TasksOrderBy) =>
            dispatch(
              setAssetVersionListParams({
                assetVersionId,
                orderBy
              })
            )
          }
          searchQuery={searchQuery}
          onSearchOpenChange={(open: boolean) => {
            dispatch(
              setAssetVersionListParams({
                assetVersionId,
                searchQuery: open ? '' : null
              })
            );
          }}
          onSearchHandler={(searchQuery: string) => {
            dispatch(
              setAssetVersionListParams({
                assetVersionId,
                searchQuery
              })
            );
          }}
          dropdownMenu={tasksMenu}
          dropdownMenuText={tasksMenu.find((x) => x?.key === listType)?.label}
        />
        <div className="media_viewer_inner b-tasks-container">
          {!showEmpty && (
            <InfiniteScroll
              loading={loading}
              hasMore={hasMore}
              loader={
                // Delayed and faded, the panel is remounted with every asset
                <SkeletonPresence show={count - taskIds.length > 0}>
                  <TaskListSkeleton
                    count={count - taskIds.length}
                    border={false}
                    height={90}
                  />
                </SkeletonPresence>
              }
              alwaysShowLoader
              next={onLoadMore}
            >
              {taskIds.map((id) => (
                <TaskItem
                  key={id}
                  taskId={id}
                  onOpenComment={onOpenComment}
                  onRemoveTask={onRemoveTask}
                />
              ))}
            </InfiniteScroll>
          )}
          {showEmpty && <NoResults />}
        </div>
        <Modal
          open={!!selectedDeleteCommentId}
          footer={null}
          centered={true}
          closeIcon={<CrossIcon />}
          width={468}
          className="media_viewer_task-modal"
          onCancel={() => setSelectedDeleteCommentId(null)}
        >
          <div className="modal_container">
            <h2 className="modal_container__title">Delete comment?</h2>
            <div className="modal_container__subtitle">
              There is a comment attached to this task. Do you want to also
              delete the comment?
            </div>
            <Row justify="end" gutter={20}>
              <Col>
                <Button
                  type="text"
                  disabled={isLoadingDeleteComment}
                  onClick={() => setSelectedDeleteCommentId(null)}
                >
                  No
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  loading={isLoadingDeleteComment}
                  onClick={onRemoveCommentById}
                >
                  Yes, delete comment
                </Button>
              </Col>
            </Row>
          </div>
        </Modal>
      </>
    </PanelSection>
  );
}

export default Tasks;
